import jwtDecode from 'jwt-decode'
import { SessionStorageKeys } from 'src/app/shared/constants/session-storage-keys'
import { StorageKeys } from 'src/app/shared/constants/storage-keys'
import { RoleDepartment } from 'src/app/shared/models/user-info'
import { environment } from 'src/environments/environment'

import { AuthService } from '../auth/auth.service'
import { createIdentityProvider } from '../identity/identity.factory'
import { KeycloakIdentityProvider } from '../identity/keycloak-identity.provider'
import { LocalStorageService } from '../storage/local-storage.service'
import { SessionStorageService } from '../storage/session-storage.service'

/** APP_INITIALIZER hook — picks Keycloak or Dex from environment.providerType. */
export const initializeKeycloak = (
  authService: AuthService,
  storage: LocalStorageService,
  sessionStorage: SessionStorageService
) => {
  return async () => {
    const identity = createIdentityProvider({
      providerType: environment.providerType,
      keycloakUrl: environment.keycloakBaseUrl,
      realmName: environment.keycloakRealm,
      keycloakAppClientId: environment.keycloakClientId,
      oidcIssuerUrl: environment.oidcIssuerUrl,
      oidcClientId: environment.oidcClientId,
      appSessionsEnabled: environment.appSessionsEnabled
    })

    identity.onAuthLogout(() => {
      // This recovers from IdP-side session loss; on a user logout it would sign them straight back in.
      if (authService.isSigningOut) return
      identity.login().catch(() => {
        /* ignore */
      })
    })
    identity.onTokenExpired(() => {
      identity.refreshToken(30).catch(() => identity.login())
    })

    authService.identity = identity

    try {
      // Init first: keycloak-js builds its adapter here and logout() needs it. No onLoad, so no redirect.
      const authenticated = await identity.init()

      // Cross-SPA SSO probe for app-session providers (Dex or Keycloak+flag): reload hydrates from the
      // kmAuth cookie offline, no IdP needed. Keycloak flag-off owns its iframe SSO, so skip.
      const bridged = environment.appSessionsEnabled ? await authService.bridgeSignIn() : null
      if (bridged && bridged.authToken) {
        // isAdmin lives inside the auth-token JWT, not on the bridge payload.
        try {
          const decoded: { isAdmin?: boolean } = jwtDecode(bridged.authToken)
          if ('isAdmin' in decoded) bridged.isAdmin = decoded.isAdmin
        } catch {
          // malformed token — setStorageValues will guard
        }
        setStorageValues(bridged)
        storage.set(StorageKeys.mustResetPassword, !!bridged['mustResetPassword'])
        storage.set(StorageKeys.isUserAuthenticated, true)
        return new Promise<boolean>(resolve => {
          authService.getCompanyInfo().subscribe(profileResponse => {
            if (profileResponse) {
              delete profileResponse.jitsuClientKey
              delete profileResponse.jitsuTrackingHost
              storage.set(StorageKeys.companyProfile, profileResponse)
            }
            // Re-run SessionCardinal on the bridge (reload) path too, so the conflict dialog
            // re-appears on refresh and can't be sidestepped by reloading (TC-16).
            authService.manageSessionCardinality(bridged.authToken).finally(() => resolve(true))
          })
        })
      }

      if (!authenticated) {
        storage.set(StorageKeys.isUserAuthenticated, false)
        // Plain login — forcing promptCredentials here would defeat cross-SPA SSO.
        await identity.login()
        return false
      }

      if (!storage.get(StorageKeys.isUserAuthenticated)) {
        if (environment.subdomain !== 'master' && identity.getToken()) await authService.loginAction()
      }

      const response = await authService.getUserInfo()
      if (response['code'] && response['code'] == 401) {
        authService.setUserInfoMessage('AccountDisabled')
        return false
      }

      // Dex forced first-login / post-edit reset. The auth-guard reads this flag
      // and routes the user to /force-reset before letting them into the app.
      storage.set(StorageKeys.mustResetPassword, !!response['mustResetPassword'])

      // Keycloak: hydrate userInfo/profile for legacy authService.keycloak.profile readers.
      if (identity instanceof KeycloakIdentityProvider) {
        const kc = identity.raw
        await kc.loadUserInfo()
        if (kc.authenticated) await kc.loadUserProfile()
      }

      setStorageValues(response)
      return new Promise<boolean>(resolve => {
        authService.getCompanyInfo().subscribe(profileResponse => {
          if (profileResponse) {
            delete profileResponse.jitsuClientKey
            delete profileResponse.jitsuTrackingHost
            storage.set(StorageKeys.companyProfile, profileResponse)
          }
          storage.set(StorageKeys.isUserAuthenticated, true)
          resolve(true)
        })
      })
    } catch {
      return false
    }
  }

  function setStorageValues(response) {
    if (!response.isAdmin) {
      authService.setUserInfoMessage('NotAdmin')
      return
    }

    const userinfo = {
      _id: response?.id,
      id: response?.id,
      isMasterEntry: response?.isMasterEntry,
      status: response?.status,
      emailId: response?.emailId,
      fullName: response?.fullName,
      username: response?.username,
      isAdmin: response?.isAdmin,
      departmentId: response?.departmentId,
      roleName: response?.role?.name,
      categoryId: response?.categoryId,
      departmentName: response?.department?.name
    }
    const { role, defaultSettings } = response
    const permissions = {
      isApprover: role?.isApprover,
      canLockContent: role?.canLockContent,
      canCreateUsers: role?.isCreateUser,
      canManageUsers: role?.isManageUsers,
      miscellaneousAccess: role?.miscellaneousAccess,
      docLibraryAccess: role?.docLibraryAccess,
      orgSettingsAccess: role?.orgSettingsAccess,
      inquiryDeskAccess: role?.inquiryDeskAccess,
      selfServiceAccess: role?.selfServiceAccess,
      hasLMSAdminAccess: role?.hasLMSAdminAccess,
      dtSessionRead: role?.dtSessionRead,
      globalSearchReplaceAccess: role?.globalSearchReplaceAccess,
      dtSessionDelete: role?.dtSessionDelete,
      translationAccess: role?.translationAccess,
      templateAccess: role?.templateAccess,
      canDeleteUsers: role?.canDeleteUsers,
      hasSearchConfigAccess: role?.hasSearchConfigAccess,
      attributeSetManage: role?.attributeSetManage,
      analyticsDashboardAccess: role?.analyticsDashboardAccess,
      groupDashboardAccess: role?.groupDashboardAccess
    }

    const departments: RoleDepartment[] = role?.departments.map(dept => ({ id: dept.id, name: dept.name })) || []
    storage.set(StorageKeys.userPermissions, permissions)
    storage.set(StorageKeys.departments, departments)
    storage.set(StorageKeys.authToken, response.authToken)
    storage.set(StorageKeys.userInfo, userinfo)
    storage.set(StorageKeys.serviceDetails, role?.servicePolicies || [])

    storage.set(StorageKeys.selectedDepartment, defaultSettings.department)

    // The language is a per-user preference kept in sync across the Agent and Admin
    // UIs by the backend. Seed it from the freshly fetched ui-info on every load so a
    // change made on the Agent side is reflected here (overriding any stale session value).
    if (defaultSettings?.language) {
      sessionStorage.set(SessionStorageKeys.selectedLanguage, defaultSettings.language)
      sessionStorage.set(SessionStorageKeys.language, defaultSettings.language.id)
    }
  }
}
